import Page from "../Page";

import classNames from 'classnames'
import css from './DashboardPage.module.scss'
import { useEffect, useMemo, useState } from "react";
import { AvailabilityPresenceState } from "../../model";
import { useSelector } from "../../store";
import { getGraphClient } from "../../api";

interface Props {
    className?: string
}

export default function DashboardPage({ className }: Props) {
    const user = useSelector(s => s.login.login)
    const userPresences = useSelector(s => s.presence.users)
    const [tenantName, setTenantName] = useState<string | null>(null)

    const byAvailability = useMemo(() => {
        const counts = new Map<string, number>()
        userPresences.forEach(presence => {
            const key = presence.availability
            counts.set(key, (counts.get(key) ?? 0) + 1)
        })

        return Array.from(counts.entries())
            .sort((left, right) => {
                const leftIndex = availabilitySortOrder.indexOf(left[0] as AvailabilityPresenceState)
                const rightIndex = availabilitySortOrder.indexOf(right[0] as AvailabilityPresenceState)
                const leftValue = leftIndex < 0 ? availabilitySortOrder.length : leftIndex
                const rightValue = rightIndex < 0 ? availabilitySortOrder.length : rightIndex
                if (leftValue !== rightValue) return leftValue - rightValue
                return left[0].localeCompare(right[0])
            })
    }, [userPresences])

    const byAvailabilityActivity = useMemo(() => {
        const counts = new Map<string, number>()
        userPresences.forEach(presence => {
            const key = `${presence.availability}/${presence.activity}`
            counts.set(key, (counts.get(key) ?? 0) + 1)
        })

        return Array.from(counts.entries())
            .sort((left, right) => left[0].localeCompare(right[0]))
    }, [userPresences])

    const tenantId = user?.tenantId || globalConfig.authenticationTenantId
    const activeTotal = useMemo(() => {
        let count = 0
        userPresences.forEach(presence => {
            if (presence.availability !== AvailabilityPresenceState.offline && presence.availability !== AvailabilityPresenceState.away) {
                count += 1
            }
        })
        return count
    }, [userPresences])

    useEffect(() => {
        let valid = true

        setTenantName(null)

        async function loadTenantName() {
            try {
                const displayName = await getGraphClient().getTenantName(tenantId)
                if (!valid) return
                setTenantName(displayName)
            } catch (e) {
                if (!valid) return
                console.error(e)
                setTenantName(null)
            }
        }

        loadTenantName()

        return () => {
            valid = false
        }
    }, [tenantId])

    return <Page className={classNames(css.DashboardPage, className)}>
        <h1>Dashboard</h1>
        <section className={css.summaryGrid}>
            <article className={css.card}>
                <h2>Tenant</h2>
                    <div className={css.detailRow}><span>Name</span><span>{tenantName || tenantId || 'Unknown'}</span></div>
                <div className={css.detailRow}><span>Tenant Id</span><span>{tenantId || 'Unknown'}</span></div>
                <div className={css.detailRow}><span>Logged-in User</span><span>{user?.name || 'Unknown'}</span></div>
                <div className={css.detailRow}><span>User UPN</span><span>{user?.upn || 'Unknown'}</span></div>
            </article>
            <article className={css.card}>
                <h2>User Counts</h2>
                <div className={css.detailRow}><span>Total</span><span>{userPresences.size}</span></div>
                    <div className={css.detailRow}><span>Active</span><span>{activeTotal}</span></div>
            </article>
        </section>

        <section className={css.countSections}>
            <article className={css.card}>
                <h2>By Availability</h2>
                {byAvailability.length === 0 && <div className={css.empty}>No users available</div>}
                {byAvailability.length > 0 && <ul className={css.countList}>
                    {byAvailability.map(([name, count]) => <li key={name}><span>{name}</span><span>{count}</span></li>)}
                </ul>}
            </article>

            <article className={css.card}>
                <h2>By Availability/Activity</h2>
                {byAvailabilityActivity.length === 0 && <div className={css.empty}>No users available</div>}
                {byAvailabilityActivity.length > 0 && <ul className={css.countList}>
                    {byAvailabilityActivity.map(([name, count]) => <li key={name}><span>{name}</span><span>{count}</span></li>)}
                </ul>}
            </article>
        </section>
    </Page>
}

const availabilitySortOrder: AvailabilityPresenceState[] = [
    AvailabilityPresenceState.available,
    AvailabilityPresenceState.busy,
    AvailabilityPresenceState.doNotDisturb,
    AvailabilityPresenceState.beRightBack,
    AvailabilityPresenceState.away,
    AvailabilityPresenceState.offline,
]

